import {
    Stack,
    Typography,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Chip,
    CircularProgress
} from "@mui/material";
import Box from "@mui/material/Box";
import React, { useState } from "react";
import searchIcon from "../../assets/images/icons/searchIcon.svg";
import { CustomSearchInput } from "../../utils/constant";
import useDebounce from "../../hooks/use.debounce";
import AvatarComponent from "../../components/avatar";
import CustomTable from "../../components/customTable";
import CustomizedSnackbars from "../../components/snackbar";
import {
    useGetPendingWithdrawalsQuery,
    useProcessWithdrawalMutation,
    useSendWithdrawalOtpMutation
} from "../../redux/api";

const WithdrawalRequests = () => {
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 500);

    const { data, isLoading, refetch } = useGetPendingWithdrawalsQuery({
        limit,
        offset: page * limit,
        search: debouncedSearch
    });

    const [processWithdrawal, { isLoading: isProcessing }] = useProcessWithdrawalMutation();
    const [sendWithdrawalOtp, { isLoading: isSendingOtp }] = useSendWithdrawalOtpMutation();

    const [snackbar, setSnackbar] = useState({
        message: "",
        severity: "success",
        open: false,
    });

    const [confirmAction, setConfirmAction] = useState(null);
    const [modifyDialog, setModifyDialog] = useState(null);
    const [modifiedAmount, setModifiedAmount] = useState("");

    // OTP state
    const [otpDialog, setOtpDialog] = useState(null); // { transactionId, action, modifiedAmount? }
    const [otp, setOtp] = useState("");

    const handleConfirmClose = () => setConfirmAction(null);
    const handleModifyClose = () => {
        setModifyDialog(null);
        setModifiedAmount("");
    };
    const handleOtpClose = () => {
        setOtpDialog(null);
        setOtp("");
    };

    // Send OTP and open OTP dialog
    const handleSendOtp = async (transactionId, action, amount = null) => {
        try {
            await sendWithdrawalOtp({ transactionId }).unwrap();
            setOtpDialog({ transactionId, action, modifiedAmount: amount });
            setOtp("");
            setSnackbar({
                message: "OTP sent to admin phone number",
                severity: "success",
                open: true
            });
        } catch (error) {
            console.error("Send OTP failed", error);
            setSnackbar({
                message: error?.data?.message || "Failed to send OTP",
                severity: "error",
                open: true
            });
        }
    };

    // Resend OTP
    const handleResendOtp = async () => {
        if (otpDialog) {
            try {
                await sendWithdrawalOtp({ transactionId: otpDialog.transactionId }).unwrap();
                setSnackbar({
                    message: "OTP resent successfully",
                    severity: "success",
                    open: true
                });
            } catch (error) {
                setSnackbar({
                    message: error?.data?.message || "Failed to resend OTP",
                    severity: "error",
                    open: true
                });
            }
        }
    };

    // Process withdrawal with OTP (for Accept/ModifyAccept)
    const handleOtpVerifyAndProcess = async () => {
        if (!otpDialog || !otp || otp.length !== 6) {
            setSnackbar({
                message: "Please enter a valid 6-digit OTP",
                severity: "error",
                open: true
            });
            return;
        }

        try {
            const body = {
                transactionId: otpDialog.transactionId,
                action: otpDialog.action,
                otp: otp
            };
            if (otpDialog.modifiedAmount !== null && otpDialog.modifiedAmount !== undefined) {
                body.modifiedAmount = otpDialog.modifiedAmount;
            }

            const response = await processWithdrawal(body).unwrap();

            if (response?.status === 'FAILURE') {
                setSnackbar({
                    message: response?.message || response?.result?.message || "OTP verification failed",
                    severity: "error",
                    open: true
                });
                return;
            }

            setSnackbar({
                message: `Withdrawal ${otpDialog.action === 'Accept' ? 'accepted' : 'modified & accepted'} successfully`,
                severity: "success",
                open: true
            });
            handleOtpClose();
            refetch();
        } catch (error) {
            console.error("Process withdrawal failed", error);
            setSnackbar({
                message: error?.data?.message || "Failed to process withdrawal. Please check OTP.",
                severity: "error",
                open: true
            });
        }
    };

    // Process rejection directly (no OTP needed)
    const handleReject = async (transactionId) => {
        try {
            await processWithdrawal({ transactionId, action: 'Reject' }).unwrap();
            setSnackbar({
                message: "Withdrawal rejected successfully",
                severity: "success",
                open: true
            });
            refetch();
        } catch (error) {
            console.error("Reject withdrawal failed", error);
            setSnackbar({
                message: error?.data?.message || "Failed to reject withdrawal",
                severity: "error",
                open: true
            });
        }
    };

    // Accept: Confirm dialog → Send OTP → OTP dialog → Process
    const handleConfirmProceed = () => {
        if (confirmAction) {
            if (confirmAction.action === 'Reject') {
                handleReject(confirmAction.transactionId);
            } else {
                handleSendOtp(confirmAction.transactionId, confirmAction.action);
            }
            setConfirmAction(null);
        }
    };

    // ModifyAccept: Modify dialog → Send OTP → OTP dialog → Process
    const handleModifyAccept = () => {
        if (modifyDialog && modifiedAmount && Number(modifiedAmount) > 0) {
            handleSendOtp(modifyDialog.transactionId, 'ModifyAccept', Number(modifiedAmount));
            handleModifyClose();
        }
    };

    const Action = (row) => {
        const { transactionId } = row;
        return (
            <div className="actBtnGrps">
                <span className="editIcon" onClick={() => setConfirmAction({ transactionId, action: 'Accept' })}>Accept</span>
                <span className="deleteIcon" onClick={() => setConfirmAction({ transactionId, action: 'Reject' })}>Reject</span>
                <span className="viewIcon" onClick={() => {
                    setModifyDialog({ transactionId, currentAmount: row.requestedAmount });
                    setModifiedAmount(row.requestedAmount);
                }}>Modify & Accept</span>
            </div>
        );
    };

    const cols = [
        "photo",
        "name",
        "currentBalance",
        "inflyxScore",
        "requestedAmount",
    ];

    const tableData = [];
    if (data?.result?.list?.length) {
        for (const key of data.result.list) {
            tableData.push({
                photo: <AvatarComponent image={key.picture} />,
                name: key.name,
                transactionId: key.id,
                currentBalance: key.currentBalance != null ? key.currentBalance : "N/A",
                inflyxScore: key.currentScore != null ? key.currentScore : "N/A",
                requestedAmount: key.amount,
            });
        }
    }

    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };

    const handleClearSearch = () => {
        setSearch("");
        setPage(0);
    };

    return (
        <>
            <CustomizedSnackbars msgData={snackbar} setMsgData={setSnackbar} style={{ maxWidth: '100%' }} />
            <Stack direction={"column"} gap={2}>
                <Stack
                    className="mainTitle flex-wrap"
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    sx={{
                        minHeight: '42px',
                        padding: '0px 16px',
                    }}
                >
                    <Typography className="page-title" variant="h1">
                        Withdrawal Requests
                    </Typography>
                    <Stack direction={"row"} gap={2}>
                        <CustomSearchInput
                            className="inputSearch sContrl p-0"
                            value={search}
                            placeholder={"Search here..."}
                            handleSearch={handleSearch}
                            clearSearch={handleClearSearch}
                            startAdornment={
                                <InputAdornment style={{ borderRadius: '50px !important' }} className="overIcon" position="start"
                                    sx={{
                                        maxHeight: 'none',
                                        height: '38px',
                                        minWidth: '38px',
                                        marginRight: '0px', background: 'none !important', borderRadius: '50px !important'
                                    }}
                                >
                                    <img src={searchIcon} height={20} width={20} alt="" />
                                </InputAdornment>
                            }
                        />
                    </Stack>
                </Stack>
                <Box sx={{ padding: '0px 16px' }}>
                    <Box
                        sx={{
                            backgroundColor: '#fff',
                            transition: 'all .5s ease-in-out',
                            position: 'relative',
                            border: '1px solid rgba(170, 170, 170, 0.3)',
                            borderRadius: '12px',
                            boxShadow: '0px 5px 5px 0px rgba(82, 63, 105, 0.05)',
                            overflow: 'hidden',
                        }}>
                        <CustomTable
                            cols={cols}
                            data={tableData}
                            isCheckbox={false}
                            actionComp={Action}
                            count={data?.result?.total || 0}
                            onPageChange={({ page, rows }) => {
                                setPage(page);
                                setLimit(rows);
                            }}
                        />
                    </Box>
                </Box>
            </Stack>

            {/* Confirmation Dialog for Accept/Reject */}
            <Dialog open={Boolean(confirmAction)} onClose={handleConfirmClose}>
                <DialogTitle>Confirm {confirmAction?.action === 'Accept' ? 'Acceptance' : 'Rejection'}</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to {confirmAction?.action === 'Accept' ? 'accept' : 'reject'} this withdrawal request?
                    </Typography>
                    {confirmAction?.action === 'Accept' && (
                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                            An OTP will be sent to the admin phone number for verification.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button onClick={handleConfirmClose} variant="outlined">Cancel</Button>
                    <Button
                        onClick={handleConfirmProceed}
                        variant="contained"
                        color={confirmAction?.action === 'Reject' ? 'error' : 'primary'}
                        disabled={isProcessing || isSendingOtp}
                        autoFocus
                    >
                        {isSendingOtp ? <CircularProgress size={20} /> : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Modify & Accept Dialog */}
            <Dialog open={Boolean(modifyDialog)} onClose={handleModifyClose} maxWidth="sm" fullWidth>
                <DialogTitle>Modify & Accept Withdrawal</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Original requested amount: <strong>{modifyDialog?.currentAmount}</strong>
                    </Typography>
                    <TextField
                        label="Modified Amount"
                        type="number"
                        fullWidth
                        value={modifiedAmount}
                        onChange={(e) => setModifiedAmount(e.target.value)}
                        helperText="Enter the approved amount to credit. An OTP will be sent for verification."
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button onClick={handleModifyClose} variant="outlined">Cancel</Button>
                    <Button
                        onClick={handleModifyAccept}
                        variant="contained"
                        disabled={isProcessing || isSendingOtp || !modifiedAmount || Number(modifiedAmount) <= 0}
                    >
                        {isSendingOtp ? <CircularProgress size={20} /> : 'Send OTP & Accept'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* OTP Verification Dialog */}
            <Dialog open={Boolean(otpDialog)} onClose={handleOtpClose} maxWidth="xs" fullWidth>
                <DialogTitle>OTP Verification</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        Enter the 6-digit OTP sent to the admin phone number to proceed with the withdrawal {otpDialog?.action === 'Accept' ? 'approval' : 'modification & approval'}.
                    </Typography>
                    <TextField
                        label="Enter OTP"
                        type="text"
                        fullWidth
                        value={otp}
                        onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                            setOtp(val);
                        }}
                        inputProps={{ maxLength: 6 }}
                        placeholder="Enter 6-digit OTP"
                        sx={{ mt: 1 }}
                        autoFocus
                    />
                    <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            size="small"
                            onClick={handleResendOtp}
                            disabled={isSendingOtp}
                            sx={{ textTransform: 'none' }}
                        >
                            {isSendingOtp ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                            Resend OTP
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ padding: 2 }}>
                    <Button onClick={handleOtpClose} variant="outlined">Cancel</Button>
                    <Button
                        onClick={handleOtpVerifyAndProcess}
                        variant="contained"
                        disabled={isProcessing || otp.length !== 6}
                    >
                        {isProcessing ? <CircularProgress size={20} /> : 'Verify & Process'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WithdrawalRequests;
